<template>
    <component class="btcdirect-button" :is="href ? 'a' : 'button'" :href="href || null" :target="target" @click.capture="clicked($event)">
        <spinner v-if="isLoading" large />
        <slot v-else />
    </component>
</template>

<script lang="ts" setup>
import Spinner from "@btcdirect/components/spinner/spinner.vue";
import { EmitEvents } from "@btcdirect/shared/src/components/types";
import { computed, ref } from "vue";

const props = defineProps<{
    href?: string;
    allowDoubleClick?: boolean;
    isLoading?: boolean;
    target?: HTMLAnchorElement["target"];
}>();

const emit = defineEmits<{ (event: "clicked"): void }>();

// TODO: check if links should open in same window
const target = computed<string>(() => {
    if (props.target) {
        return props.target;
    }
    if (props.href) {
        return "_blank";
    }

    return null;
});

const preventingClicks = ref<boolean>(false);
const clicked = (event: MouseEvent): void => {
    if (!!props.href) {
        return;
    }

    if (!props.allowDoubleClick) {
        emit(EmitEvents.CLICKED);
        return;
    }

    if (preventingClicks.value) {
        event.preventDefault();
        return;
    }

    preventingClicks.value = true;
    emit(EmitEvents.CLICKED);

    setTimeout(
        (() => {
            preventingClicks.value = false;
        }) as TimerHandler,
        500
    );
};
</script>

<style lang="scss" scoped>
@use "@@scss/flex";
@use "@@scss/fonts";
@use "@@scss/transitions";
@use "@@scss/variables";

.btcdirect-button {
    @include flex.flex;
    @include flex.align(center, center);
    @include fonts.gt-walsheim-medium;
    @include transitions.transition((background-color, border-color, color));
    border-radius: variables.$input-border-radius;
    height: variables.$input-height;
    text-decoration: none;
    width: 100%;

    &:not(.link) {
        @include fonts.font(1.125rem, default, white);
        @include flex.flex;
        @include flex.align(center, center);
        border: 1px solid;
        column-gap: 0.5em;
    }

    &.btcdirect-button--small {
        @include fonts.font(1rem, 1.375rem);
        height: calc(variables.$input-height - 0.5rem);
    }

    /** Primary and secondary buttons */
    &:not(.link):not(.white):not(:disabled) {
        &:not(:hover):not(:focus) {
            &:not(.borderless) {
                border-color: var(--button-background-color);
            }

            &.borderless {
                border-color: transparent;
            }
        }

        &:hover {
            border-color: var(--button-hover-background-color);
        }

        &:focus {
            border-color: var(--button-focus-background-color);
        }
    }

    /** Primary button */
    &:not(.secondary):not(.white):not(.link):not(:disabled) {
        &:not(:hover):not(:focus) {
            background-color: var(--button-background-color);
        }

        &:hover {
            background-color: var(--button-hover-background-color);
        }

        &:focus {
            background-color: var(--button-focus-background-color);
        }
    }

    &.secondary:not(:disabled) {
        background-color: transparent;
        color: var(--btcdirect-blue);

        &:not(:hover):not(:focus) {
            color: var(--button-background-color);
        }

        &:hover {
            color: var(--button-hover-background-color);
        }

        &:focus {
            color: var(--button-focus-background-color);
        }
    }

    &.white {
        background-color: var(--white-button-background-color);
        color: variables.$btcdirect-blue;
        border-color: var(--white-button-background-color);

        &:not(:hover):not(:focus) {
            background-color: var(--white-button-background-color);
        }

        &:hover {
            background-color: var(--white-button-hover-background-color);
        }

        &:focus {
            background-color: var(--white-button-hover-background-color);
        }
    }

    &.link {
        @include flex.flex;
        @include flex.align(center, center);
        @include fonts.gt-walsheim-medium;

        &:not(.large) {
            @include fonts.font(1.125rem, 1.75rem, var(--btcdirect-blue));
        }

        &.large {
            @include fonts.font(1.125rem, default, var(--text-color-lighter));
            height: calc(variables.$input-height);
        }

        &.inline {
            display: inline;
        }

        &:hover,
        &:focus {
            text-decoration: underline;
        }
    }

    &:disabled {
        cursor: not-allowed;
        background-color: var(--secondary-button-border-color);
        border-color: var(--secondary-button-border-color);
    }

    &:hover:not(:disabled) {
        cursor: pointer;
    }

    &:focus {
        outline: none;
    }
}

button.btcdirect-button.link {
    &:not(.large) {
        @include fonts.font(1rem, 1.375rem);
    }

    color: var(--btcdirect-blue);
    display: inline;
    height: unset;
    width: unset;
    padding: unset;
    border: unset;
    background: unset;
}
</style>
